import { Category, FeedId, NodeId, RestfulStory, SocketStory, Source, SourceGroup, StoryId } from './entities';
import { Story } from './story';
import { NewsFeed } from './feed';
import { AdvancedNewsManager } from './manager';
import { ContentTypes, PressRelease } from './ingress';
import { deepEqual } from '@benzinga/utils';
import { ListenableSubscribable } from '@benzinga/subscribable';

interface UpdateCategoriesEvent {
  type: 'news:updated_categories';
  categories: Category[];
}

interface UpdatePressReleaseEvent {
  type: 'news:updated_press_releases';
  pressReleases: PressRelease[];
}

interface UpdateSourceEvent {
  type: 'news:updated_sources';
  sources: Source[];
}

interface UpdateSourceGroupEvent {
  type: 'news:updated_sources_groups';
  sourcesGroups: SourceGroup[];
}
interface NewStoryEvent {
  type: 'news:new_story';
  story: Story;
}

interface NewStoriesEvent {
  type: 'news:new_stories' | 'news:historic_stories';
  stories: Story[];
}

export type NewsStoreEvent =
  | NewStoryEvent
  | NewStoriesEvent
  | UpdateCategoriesEvent
  | UpdatePressReleaseEvent
  | UpdateSourceEvent
  | UpdateSourceGroupEvent;

export class NewsStore extends ListenableSubscribable<NewsStoreEvent> {
  private stories: Map<NodeId, WeakRef<Story>> = new Map();
  private storiesFromStoryId: Map<StoryId, WeakRef<Story>> = new Map();
  private activeNewsFeeds: Map<FeedId, NewsFeed> = new Map();

  private categories?: Category[];
  private categoriesList?: Category[];
  private pressReleases?: PressRelease[];
  private sources?: Source[];
  private sourcesGroups?: SourceGroup[];

  private contentTypes?: ContentTypes;
  private manager: AdvancedNewsManager;

  constructor(manager: AdvancedNewsManager) {
    super();
    this.manager = manager;
  }

  public processMultipleNewStories = async (
    quoteStories: (SocketStory | RestfulStory)[],
    cache = true,
  ): Promise<Story[]> => {
    const stories = await Promise.all(quoteStories.map(story => this.processStoryInternal(story, cache)));
    this.dispatch({
      stories,
      type: 'news:new_stories',
    });
    return stories;
  };

  public processMultipleHistoricStories = async (
    quoteStories: (SocketStory | RestfulStory)[],
    cache = true,
  ): Promise<Story[]> => {
    if (!Array.isArray(quoteStories)) return [];
    const stories = await Promise.all(quoteStories.map(story => this.processStoryInternal(story, cache)));
    this.dispatch({
      stories,
      type: 'news:historic_stories',
    });
    return stories;
  };

  public processStory = async (quoteStory: SocketStory | RestfulStory, cache = true): Promise<Story> => {
    const story = await this.processStoryInternal(quoteStory, cache);
    this.dispatch({
      story,
      type: 'news:new_story',
    });
    return story;
  };

  public getStory = (id: NodeId): Story | undefined => this.stories.get(id)?.deref();

  public addActiveNewsFeed = (feedId: FeedId, newsfeed: NewsFeed): void => {
    this.activeNewsFeeds.set(feedId, newsfeed);
  };

  public removeActiveNewsFeed = (feedId: FeedId): void => {
    this.activeNewsFeeds.delete(feedId);
  };

  public getActiveNewsFeeds = (): NewsFeed[] => {
    return Array.from(this.activeNewsFeeds.values());
  };

  public getCategoriesList = (): Category[] | undefined => {
    return this.categoriesList;
  };

  public getCategories = (): Category[] | undefined => {
    return this.categories;
  };

  public updateCategories = (categories: Category[]): boolean => {
    if (this.categories && deepEqual(this.categories, categories)) {
      return false;
    } else {
      this.categories = categories;

      const getChildren = (categories: Category[]): Category[] =>
        categories.flatMap(item => (item.children ? [item, ...getChildren(item.children)] : [item]));
      this.categoriesList = categories ? getChildren(categories) : undefined;

      this.dispatch({
        categories,
        type: 'news:updated_categories',
      });
      return true;
    }
  };

  public getPressRelease = (): PressRelease[] | undefined => {
    return this.pressReleases;
  };

  public updatePressRelease = (pressReleases: PressRelease[]): boolean => {
    if (this.pressReleases && deepEqual(this.pressReleases, pressReleases)) {
      return false;
    } else {
      this.pressReleases = pressReleases;

      this.dispatch({
        pressReleases,
        type: 'news:updated_press_releases',
      });
      return true;
    }
  };

  public getSources = (): Source[] | undefined => {
    return this.sources;
  };

  public updateSources = (sources: Source[]): boolean => {
    if (this.sources && deepEqual(this.sources, sources)) {
      return false;
    } else {
      this.sources = sources;

      this.dispatch({
        sources,
        type: 'news:updated_sources',
      });
      return true;
    }
  };

  public updateSourcesGroups = (sourcesGroups: SourceGroup[]): boolean => {
    if (this.sourcesGroups && deepEqual(this.sourcesGroups, sourcesGroups)) {
      return false;
    } else {
      this.sourcesGroups = sourcesGroups;

      this.dispatch({
        sourcesGroups,
        type: 'news:updated_sources_groups',
      });
      return true;
    }
  };

  public getContentTypes = (): ContentTypes | undefined => {
    return this.contentTypes;
  };

  public setContentTypes = (contentTypes: ContentTypes): void => {
    this.contentTypes = contentTypes;

    this.updateCategories(contentTypes.categories);
    this.updatePressRelease(contentTypes.pressRelease);
    this.updateSources(contentTypes.sources);
    this.updateSourcesGroups(contentTypes.sourcesGroups);
  };

  private processStoryInternal = async (quoteStory: SocketStory | RestfulStory, cache = true): Promise<Story> => {
    const sources = await this.manager.getSources();

    // Skip Cache and return new story
    if (cache === false) {
      return new Story(quoteStory, this.manager, sources.ok);
    }

    const cachedStory = this.getStory(quoteStory.NodeID);

    if (cachedStory) {
      if (cachedStory.getVersionId() === quoteStory.VersionID) {
        return cachedStory.updateStory(quoteStory, sources.ok);
      } else if (cachedStory.getUpdatedAtDate() <= new Date(quoteStory.UpdatedAt)) {
        return cachedStory.updateStory(quoteStory, sources.ok);
      } else {
        return cachedStory;
      }
    } else {
      const story = new Story(quoteStory, this.manager, sources.ok);
      this.setStory(new WeakRef(story));
      return story;
    }
  };

  private setStory = (storyRef: WeakRef<Story>) => {
    const story = storyRef.deref();
    if (story) {
      this.stories.set(story.getNodeId(), storyRef);
      this.storiesFromStoryId.set(story.getStoryId(), storyRef);
    }
  };
}
